import React, { useEffect } from "react";
import { Provider, useSelector, useDispatch } from "react-redux";
import { store, persistor } from "./store";
import { PersistGate } from "redux-persist/integration/react";
import {
  HashRouter as Router,
  Switch,
  Redirect,
  useHistory 
} from "react-router-dom";
import "moment/locale/es";
import { Route } from "components";
import { RootState } from "reducers";
import { Socket, SocketEvents, Constants, Globals } from 'utils';
import { AuthService } from 'services';
import * as Jetemit from 'jetemit';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Public
import Home from 'screens/home';

// Auth
import Login from "screens/auth/login/login";
import Recover from "screens/auth/login/recover";
import Reset from "screens/auth/login/reset";
import Profile from "screens/profile";

// Admin
import AdminHome from 'screens/admin/home/home';
import AdminUsers from 'screens/admin/users/users';
import AdminPlans from 'screens/admin/plans/plans';
import AdminVerificationPlans from 'screens/admin/plans/verification-plans';
import AdminDocumentPlans from 'screens/admin/plans/documents/plans';
import AdminBillPlans from 'screens/admin/plans/bills/plans';
import AdminReportDocuments from 'screens/admin/documents/documents';
import AdminReportViewDocument from 'screens/admin/documents/view';
import AdminDocumentsIndex from 'screens/admin/documents/index';
import AdminVerificationsIndex from 'screens/admin/verifications/index';
import AdminReportVerifications from 'screens/admin/verifications/verifications';
import AdminReportBlackList from 'screens/admin/verifications/black-list/black-list';
import AdminViewVerification from 'screens/admin/verifications/view';
import AdminModerators from 'screens/admin/moderators/moderators';
import AdminVerificationsConfig from 'screens/client/config/config';
import AdminDocumentsConfig from 'screens/client/config/config';
import AdminBillsConfig from 'screens/client/config/config';
import AdminSystemMovements from 'screens/admin/system-movements/system-movements';
import AdminBills from 'screens/admin/bills/bills';
import AdminReportBills from 'screens/admin/bills/report/report';
import AdminReportViewBill from 'screens/admin/bills/report/view';
import AdminUploadBills from 'screens/admin/bills/upload';
import AdminExportBills from 'screens/admin/bills/export';
import AdminCompanyBills from 'screens/admin/bills/company';

// Client
import ClientHome from 'screens/client/home/home';
import ClientApi from 'screens/client/api/api';
import ClientVerificationsIndex from 'screens/admin/verifications/index';
import ClientReportVerifications from 'screens/client/verifications/verifications';
import ClientReportBlackList from 'screens/client/verifications/black-list/black-list';
import ClientDocumentsIndex from 'screens/admin/documents/index';
import ClientReportDocuments from 'screens/admin/documents/documents';
import ClientReportViewDocument from 'screens/admin/documents/view';
import ClientVerificationsConfig from 'screens/client/config/config';
import ClientDocumentsConfig from 'screens/client/config/config';
import ClientBillsConfig from 'screens/client/config/config';
import ClientModerator from 'screens/client/moderators/moderators';
import ClientSystemMovements from 'screens/admin/system-movements/system-movements';
import ClientBills from 'screens/admin/bills/bills';
import ClientReportBills from 'screens/admin/bills/report/report';
import ClientReportViewBill from 'screens/admin/bills/report/view';
import ClientUploadBills from 'screens/admin/bills/upload';
import ClientExportBills from 'screens/admin/bills/export';
import ClientCompanyBills from 'screens/admin/bills/company';

// Scripts
import "jquery/dist/jquery.min.js";
import "bootstrap/dist/js/bootstrap.min.js";

// Styles
import "bootstrap/dist/css/bootstrap.min.css";
import "font-awesome/css/font-awesome.min.css";
import "./scss/main.scss";

const Container = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const toastIdRef = React.useRef<any>(null);

  useEffect(() => {
    const getUser = async () => {
      const user = store.getState().user;
      if (user) {
        const res: any = await AuthService.checkUser({
          withoutLoading: true
        });
        if (res.user) {
          if (res.user?.unique_id != user?.unique_id) {
            Globals.showWarning("Su sesión ha expirado. Por favor, vuelva a ingresar");
            close();
          }
          else {
            dispatch({
              type: 'SET_USER',
              payload: res.user
            });
          }
        }
        else {
          Globals.showError("Lo sentimos, su usuario ha sido eliminado");
          close();
        }
      }
    }
    getUser();

    const close = async () => {
      await dispatch({
        type: 'SET_USER',
        payload: null
      }); 
      await dispatch({
        type: 'SET_ACCESS_TOKEN',
        payload: null
      }); 
      history.replace('/logout');
    }

    Socket.on(SocketEvents.UPDATE_USER,(data: any) => {
      const user = store.getState().user;
      if (user != null && user?.id == data.user_id) {
        getUser();
      }
    });

    Socket.on(SocketEvents.CHATS.MESSAGE,(data: any) => {
      Jetemit.emit(SocketEvents.CHATS.MESSAGE,data);
    });

    Socket.on(SocketEvents.UPLOAD.FINISH,(params: any) => {
      const user = store.getState().user;

      if (toastIdRef.current && params?.user_id == user?.id) {
        toast.update(toastIdRef.current, {
          render: "¡Proceso finalizado! Facturas procesadas y guardadas con éxito.",
          type: "success",
          isLoading: false,
          autoClose: 2000,
          position: "bottom-right",
          theme: "dark"
        });
        toastIdRef.current = null;
      }
    });

    Socket.on(SocketEvents.UPLOAD.START,(params: any) => {
      const user = store.getState().user;

      if (!toastIdRef.current && params?.user_id == user?.id) {
        toastIdRef.current = toast.loading("Procesando y cargando comprobantes", {
          position: "bottom-right",
          theme: "dark"
        });
      }
    });

    Socket.on(SocketEvents.UPLOAD.ERROR, (params: any) => {
      const user = store.getState().user;
      
      if (toastIdRef.current && params?.user_id == user?.id) {
        toastIdRef.current = toast.update(toastIdRef.current, {
          render: "Ocurrió un error al procesar las facturas. Intente nuevamente.",
          type: "error",
          isLoading: false,
          autoClose: 4000,
          position: "bottom-right",
          theme: "dark",
        });
        toastIdRef.current = null;
      }
    });
  },[]);

  return (
    <React.Fragment>
      <Switch>
        {/* Public */}
        <Route exact path="/" component={ Home } />
        <Route exact auth path="/home" component={ Home } />

        {/* Auth */}
        <Route exact noAuth path="/login" component={ Login } />
        <Route exact auth path="/profile" component={ Profile } />
        <Route exact noAuth path="/recover" component={ Recover } />
        <Route exact noAuth path="/recover/:code" component={ Reset } />

        {/* Admin */}
        <Route exact admin path="/admin/home" component={ AdminHome } permission={ Constants.MODULES.ADMIN_HOME } />
        <Route exact admin path="/admin/users" component={ AdminUsers } permission={ Constants.MODULES.USERS } />
        <Route exact admin path="/admin/plans" component={ AdminPlans } permission={ [Constants.MODULES.VERIFICATION_PLANS,Constants.MODULES.DOCUMENT_PLANS] } />
        <Route exact admin path="/admin/plans/verifications" component={ AdminVerificationPlans } permission={ Constants.MODULES.VERIFICATION_PLANS } />
        <Route exact admin path="/admin/verifications" component={ AdminVerificationsIndex } permission={ [
          Constants.MODULES.ADMIN_KYC,
          Constants.MODULES.ADMIN_BLACK_LIST
        ] } />
        <Route exact admin path="/admin/documents" component={ AdminDocumentsIndex } permission={ Constants.MODULES.ADMIN_DOCUMENTS } />
        <Route exact admin path="/admin/verifications/config" component={ AdminVerificationsConfig } permission={ Constants.MODULES.ADMIN_CONFIG } />
        <Route exact admin path="/admin/bills/config" component={ AdminBillsConfig } permission={ Constants.MODULES.ADMIN_CONFIG } />
        <Route exact admin path="/admin/documents/config" component={ AdminDocumentsConfig } permission={ Constants.MODULES.ADMIN_CONFIG } />
        <Route exact admin path="/admin/verifications/report" component={ AdminReportVerifications } permission={ Constants.MODULES.ADMIN_KYC } />
        <Route exact admin path="/admin/verifications/report/view/:id" component={ AdminViewVerification } permission={ Constants.MODULES.ADMIN_KYC } />
        <Route exact admin path="/admin/verifications/black-list" component={ AdminReportBlackList } permission={ Constants.MODULES.ADMIN_BLACK_LIST } />
        <Route exact admin path="/admin/documents/report" component={ AdminReportDocuments } permission={ Constants.MODULES.ADMIN_DOCUMENTS } />
        <Route exact admin path="/admin/documents/report/view/:id" component={ AdminReportViewDocument } permission={ Constants.MODULES.ADMIN_DOCUMENTS } />
        <Route exact admin path="/admin/bills" component={ AdminBills } permission={ Constants.MODULES.ADMIN_BILLS } />
        <Route exact admin path="/admin/bills/report" component={ AdminReportBills } permission={ Constants.MODULES.ADMIN_BILLS } />
        <Route exact admin path="/admin/bills/report/view/:id" component={ AdminReportViewBill } permission={ Constants.MODULES.ADMIN_BILLS } />
        <Route exact admin path="/admin/bills/upload" component={ AdminUploadBills } permission={ Constants.MODULES.ADMIN_BILLS } />
        <Route exact admin path="/admin/bills/export" component={ AdminExportBills } permission={ Constants.MODULES.ADMIN_BILLS } />
        <Route exact admin path="/admin/bills/company" component={ AdminCompanyBills } permission={ Constants.MODULES.ADMIN_BILLS } />
        <Route exact admin path="/admin/plans/documents" component={ AdminDocumentPlans } permission={ Constants.MODULES.DOCUMENT_PLANS } />
        <Route exact admin path="/admin/plans/bills" component={ AdminBillPlans } permission={ Constants.MODULES.BILL_PLANS } />
        <Route exact admin path="/admin/moderators" component={ AdminModerators } permission={ Constants.MODULES.ADMIN_MODERATORS } />
        <Route exact admin path="/admin/system-movements" component={ AdminSystemMovements } permission={ Constants.MODULES.ADMIN_SYSTEM_MOVEMENTS } />

        {/* Client */}
        <Route exact client path="/client/home" component={ ClientHome } permission={ Constants.MODULES.GRAPHICS } />
        <Route exact client path="/client/config/api" component={ ClientApi } permission={ Constants.MODULES.CONFIG } />
        <Route exact client path="/client/verifications" kyc component={ ClientVerificationsIndex } permission={ [
          Constants.MODULES.KYC,
          Constants.MODULES.BLACK_LIST,
        ] } />
        <Route exact client path="/client/documents" documents component={ ClientDocumentsIndex } permission={ Constants.MODULES.DOCUMENTS } />
        <Route exact client path="/client/verifications/config" kyc component={ ClientVerificationsConfig } permission={ Constants.MODULES.CONFIG } />
        <Route exact client path="/client/bills/config" bills component={ ClientBillsConfig } permission={ Constants.MODULES.CONFIG } />
        <Route exact client path="/client/documents/config" documents component={ ClientDocumentsConfig } permission={ Constants.MODULES.CONFIG } />
        <Route exact client path="/client/verifications/report" kyc component={ ClientReportVerifications } permission={ Constants.MODULES.KYC } />
        <Route exact client path="/client/verifications/report/view/:id" kyc component={ AdminViewVerification } permission={ Constants.MODULES.KYC } />
        <Route exact client path="/client/verifications/black-list" kyc component={ ClientReportBlackList } permission={ Constants.MODULES.BLACK_LIST } />
        <Route exact client path="/client/documents/report" documents component={ ClientReportDocuments } permission={ Constants.MODULES.DOCUMENTS } />
        <Route exact client path="/client/documents/report/view/:id" documents component={ ClientReportViewDocument } permission={ Constants.MODULES.DOCUMENTS } />
        <Route exact client path="/client/bills" bills component={ ClientBills } permission={ Constants.MODULES.BILLS } />
        <Route exact client path="/client/bills/report" bills component={ ClientReportBills } permission={ Constants.MODULES.BILLS } />
        <Route exact client path="/client/bills/report/view/:id" bills component={ ClientReportViewBill } permission={ Constants.MODULES.BILLS } />
        <Route exact client path="/client/bills/export" bills component={ ClientExportBills } permission={ Constants.MODULES.BILLS } />
        <Route exact client path="/client/bills/upload" bills component={ ClientUploadBills } permission={ Constants.MODULES.BILLS } />
        <Route exact client path="/client/bills/company" bills component={ ClientCompanyBills } permission={ Constants.MODULES.BILLS } />
        <Route exact client path="/client/moderators" component={ ClientModerator } permission={ Constants.MODULES.MODERATORS } />
        <Route exact client path="/client/system-movements" component={ ClientSystemMovements } permission={ Constants.MODULES.SYSTEM_MOVEMENTS } />

        <Redirect to="/" />
      </Switch>

      <ToastContainer 
        theme="dark"
      />
    </React.Fragment>
  )
}

const App = () => (
  <Provider store={ store }>
    <PersistGate loading={ null } persistor={ persistor }>
      <Router>
        <Container />
      </Router>
    </PersistGate>
  </Provider>
);

export default App;
